// Usage. The charts and the breakdown draw the content files' sample data and
// say so; only "This session" is real, from this session's charges and
// refunds. See Design > Exchange, "Money".

import { useState } from "preact/hooks";
import { BarList, DetailLayout, Metric, PageHeader, PillGroup, RailPanel, SampleMark } from "../../../../ui/src/index.ts";
import { usd } from "../../domain/money.ts";
import { useStore, type LedgerEntry } from "../store.ts";
import { NotConnected } from "../ui.tsx";

const SAMPLE_TITLE = "Sample data: the Consumer API has no usage route yet, so this is an example, not your usage";

/** Answers, spend and refunds in this session, from the ledger. Charges are negative amounts. */
export function sessionTotals(ledger: LedgerEntry[]) {
  let answers = 0, spent = 0, refunded = 0;
  for (const e of ledger) {
    if (e.type === "Charge") { answers++; spent -= e.amount; } else refunded += e.amount;
  }
  return { answers, spent, refunded };
}

const daysAgo = (n: number) => (n === 0 ? "Today" : n === 1 ? "1 day ago" : `${n} days ago`);

/** A daily bar chart. Every bar states its value in its title and in text for assistive tech; the bar's height is only a picture of it. */
function Chart({ title, days, total }: { title: string; days: number[]; total?: string }) {
  const max = Math.max(1, ...days);
  return (
    <section class="cs-usage-chart" aria-label={title}>
      <div class="cs-usage-chart__head">
        <h2>{title}<SampleMark title={SAMPLE_TITLE} /></h2>
        {total !== undefined && <strong class="cs-usage-chart__total">{total}</strong>}
      </div>
      <div class="cs-usage-chart__plot">
        <div class="cs-usage-chart__scale" aria-hidden="true"><span>{max}</span><span>0</span></div>
        <ol class="cs-usage-bars" aria-label={`${title} per day, sample data`}>
          {days.map((d, i) => {
            const label = `${daysAgo(days.length - 1 - i)}: ${d}`;
            return (
              <li key={i} title={label}>
                <span class="cs-usage-bars__bar" style={{ height: `${(d / max) * 100}%` }} aria-hidden="true" />
                <span class="ui-sr-only">{label}</span>
              </li>
            );
          })}
        </ol>
        <div class="cs-usage-chart__axis" aria-hidden="true"><span>{days.length - 1} days ago</span><span>Today</span></div>
      </div>
    </section>
  );
}

export function Usage() {
  const sample = useStore((s) => s.content.sample);
  const ledger = useStore((s) => s.ledger);
  const balance = useStore((s) => s.balance);
  const [tab, setTab] = useState<"hub" | "key">("hub");
  const days = sample.usageDays;
  const totals = sessionTotals(ledger);
  const rail = (
    <RailPanel title={<>Breakdown<SampleMark title={SAMPLE_TITLE} /></>}>
      <PillGroup
        label="Group by"
        options={[{ value: "hub", label: "Hub" }, { value: "key", label: "API key" }]}
        value={tab}
        onChange={setTab}
      />
      {tab === "hub" ? (
        <div class="cs-usage-breakdown">
          <BarList label="Answers per hub, sample data" items={sample.breakdown.map((b) => ({ label: b.label, value: b.answers, valueLabel: String(b.answers) }))} />
          <p class="cs-usage-foot">Answers per hub, sample data.</p>
        </div>
      ) : (
        <p class="cs-usage-foot cs-usage-breakdown">API keys aren't connected in this prototype. Requests use your WarmHub token.</p>
      )}
    </RailPanel>
  );
  return (
    <>
      <PageHeader
        title={<strong>Usage</strong>}
        action={
          <div class="cs-usage-actions">
            <NotConnected size="sm" label="Filter by key">All keys</NotConnected>
            <NotConnected size="sm" label="Period">Last 30 days</NotConnected>
            <NotConnected size="sm" label="Group by">Daily</NotConnected>
            <NotConnected size="sm" label="Export">Export</NotConnected>
          </div>
        }
      />
      <DetailLayout rail={rail}>
        <section class="cs-usage-section" aria-label="This session">
          <div class="cs-usage-section__head">
            <h2>This session</h2>
            <p>Real charges and refunds from your requests since you opened the Console.</p>
          </div>
          <div class="cs-usage-summary">
            <Metric label="Answers" value={totals.answers} context="Charged this session" />
            <Metric label="Spent" value={usd(totals.spent)} context="Charged for answers" />
            <Metric label="Refunded" value={usd(totals.refunded)} context="Credited back" />
            <Metric label="Balance" value={usd(balance)} context={balance === null ? "Updates after a request" : "Credits left"} />
          </div>
        </section>

        <section class="cs-usage-section">
          <p class="cs-usage-note">The charts and breakdown below show sample data, not your usage.</p>
          <Chart title="Spend*" days={days} />
          <Chart title="Answers" days={days} total={String(days.reduce((a, b) => a + b, 0))} />
          <Chart title="Requests" days={days} />
          <p class="cs-usage-foot">*Spend is what you were charged: only answers are charged, at each hub's ask. All three charts draw the same sample series.</p>
        </section>
      </DetailLayout>
    </>
  );
}
